import { api } from '../api/client';
import { andFilter } from '../utils/lookupFilter';
import { lookup, normalizeCode } from '../utils/recordCodes';
import { resolveRecordCode } from './recordCodes';

/**
 * Turning a decoded string into a record.
 *
 * THROUGH THE API, NOT THROUGH THE POOL. `InputCardLookup` loads its options
 * client-side with an inline cap, so matching a scan against that pool would
 * silently miss every record past the cap — and "the scanner cannot find the
 * truck" would be indistinguishable from "the truck is not in the system".
 * `GET /api/items?q=` is exact and unbounded by that cap, and KQL has an `id`
 * system field, so the no-match-element case is one request too.
 *
 * SEVERAL MATCHES ARE ALWAYS SHOWN. A barcode scan that silently picks the
 * first of two records is the worst failure this feature has: it is a wrong
 * write that looks like a right one. So this returns every hit it found and
 * the caller asks.
 */

// Enough to prove "more than one" and to list them; a scan that matches
// dozens is a misconfiguration the reader should see, not page through.
const MAX_HITS = 10;

/** KQL string literal — the quote and the backslash are the only escapes. */
export const kqlLiteral = value => `"${String(value == null ? '' : value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

/**
 * A LABEL THIS PLATFORM PRINTED. A new barcode block's payload is
 * `{{_url}}`, which renders as
 * `https://<origin>/collections/<id>/items/<id>` — so the commonest symbol
 * in the product decodes to a deep link, not to a bare id. KQL's `id`
 * system field keeps only 24-hex values and discards everything else, so
 * matching the whole URL against `id` matches NOTHING: every scan of a
 * keepr label would answer "nothing here matches https://…", with no way
 * for an author to repair it from the product.
 *
 * The id is pulled out of the link instead. Anything that is not one of our
 * own item links is left exactly as it was scanned.
 */
const ITEM_LINK = /\/collections\/[0-9a-fA-F]{24}\/items\/([0-9a-fA-F]{24})(?:[/?#]|$)/;

export const itemIdFromLink = value => {
	const text = String(value == null ? '' : value).trim();
	if (!/^https?:\/\//i.test(text)) return null;
	const found = ITEM_LINK.exec(text);
	if (found) return found[1];
	// An item's short URL with its id in place of a code (/i/<id>).
	const byId = SHORT_ITEM_LINK.exec(text);
	return byId && /^[0-9a-f]{24}$/i.test(byId[1]) ? byId[1].toLowerCase() : null;
};

// An item's short URL, /i/<code> (KPE-002) — in capitals too: a QR code
// carries a record's bare short URL in capitals (utils/shareUrls qrValue).
const SHORT_ITEM_LINK = /^https?:\/\/[^/]+\/i\/([0-9a-z-]+)\/?(?:[?#]|$)/i;

/**
 * The item id a scanned value names, when it is one of our item links: the
 * long one, /i/<id>, or /i/<code> — a code read through what this browser
 * already knows, else asked (GET /api/codes/item/{code}). Null for anything
 * else, or a code that opens nothing for this reader.
 */
export async function itemIdFromScan(value) {
	const direct = itemIdFromLink(value);
	if (direct) return direct;
	const text = String(value == null ? '' : value).trim();
	const short = SHORT_ITEM_LINK.exec(text);
	const code = short ? normalizeCode(short[1]) : null;
	if (!code) return null;
	const known = lookup('i', code);
	if (known) return known.id;
	try {
		return (await resolveRecordCode('item', code)).id;
	} catch {
		return null;
	}
}

/**
 * resolveScan({ collectionId, cardId, match, value })
 *   -> { hits: [item], query }
 *
 * `match` names the element on the TARGET card the decoded value is compared
 * against. Absent, the value is treated as the record's own id.
 *
 * `filter` is the lookup's picker filter (utils/lookupFilter.js), ANDed in
 * so a scan searches the same pool the dropdown offers. The caller decides
 * what a miss means: outside the filter is not "nothing here".
 */
export async function resolveScan({ collectionId, cardId, match, value, filter = '', signal } = {}) {
	const decoded = String(value == null ? '' : value).trim();
	if (!decoded) return { hits: [], query: '' };

	const field = match && String(match).trim() ? String(match).trim() : 'id';
	// Only when the value IS the record's id: a `match` element names a
	// field of the author's own, and a URL in it is a URL.
	const subject = field === 'id' ? ((await itemIdFromScan(decoded)) || decoded) : decoded;
	const query = andFilter(filter, `${field} = ${kqlLiteral(subject)}`);

	// THE SAME POOL THE DROPDOWN BESIDE IT SHOWS. InputCardLookup loads its
	// options with include_descendants, so a 'Vehicle' lookup lists the
	// Trucks and Trailers under it. Without this the scan searches the
	// parent card alone and reports "nothing matches" for a record the
	// reader can see one control away — which is the exact ambiguity going
	// through the API was meant to remove.
	const params = { q: query, limit: MAX_HITS, include_descendants: true };
	// The lookup's filter is stored text: read as the picker and the strict
	// gate read it (keepr-api `q_stored`).
	if (String(filter || '').trim()) params.q_stored = true;
	if (collectionId) params.collection_id = collectionId;
	if (cardId) params.card_id = cardId;

	const response = await api.get('/items', { params, signal });
	const data = response?.data;
	const hits = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
	return { hits: hits.slice(0, MAX_HITS), query };
}

/**
 * The fixed affixes a printer adds and the field does not want — 'P/N:' in
 * front of every part number, a trailing newline some scanners append.
 * Applied before anything else sees the value, so a match, a stored text
 * value and what the reader is shown all agree.
 */
export function applyStrip(value, strip) {
	let out = String(value == null ? '' : value);
	if (!strip) return out.trim();
	if (strip.prefix && out.startsWith(strip.prefix)) out = out.slice(strip.prefix.length);
	if (strip.suffix && out.endsWith(strip.suffix)) out = out.slice(0, out.length - strip.suffix.length);
	return out.trim();
}

/** The `options.scan` value, normalized from its `true`-or-object shape. */
export function scanConfig(options) {
	const raw = options?.scan;
	if (!raw) return null;
	if (raw === true) return { formats: [], match: null, strip: null };
	return {
		formats: Array.isArray(raw.formats) ? raw.formats : [],
		match: raw.match || null,
		strip: raw.strip || null,
	};
}
